import { useCallback, useState } from "react";
import { message } from "antd";
import { getToken, getCurrentShopData } from "@/utils/storage";
import { usePostBaobPcSupplyMakePrintSign } from "@/openapi/baob/pc.Supply/api.client.hooks";
import type { PostBaobPcSupplyMakePrintSign200 } from "@/openapi/baob/pc.Supply/api.client.axios";

// Yizhe system domain configuration
export const YIZHE_DOMAIN_CONFIG: Record<string, string> = {
  dev: "http://scm-test3.yizheyun.cn",
  test: "http://scm-test3.yizheyun.cn",
  pre: "https://scm-pre.yizheyun.com",
  prod: "https://scm.yizheyun.com",
};

// Yizhe system path configuration
export const YIZHE_PATHS = {
  PRINT_CENTER_STYLE_MANAGEMENT: "/print-center/style-management",
} as const;

// Get the Yizhe system domain for the current environment
export const getYizheDomain = (): string => {
  console.log("REACT_APP_ENV", REACT_APP_ENV);
  const currentEnv = REACT_APP_ENV || "prod";
  return YIZHE_DOMAIN_CONFIG[currentEnv] || YIZHE_DOMAIN_CONFIG.prod;
};

export interface YizheJumpOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

// Get print signature
const fetchMakePrintSign = async (
  mutateAsync: (variables: {
    url_prefix: string;
  }) => Promise<PostBaobPcSupplyMakePrintSign200>,
): Promise<string> => {
  try {
    const response = await mutateAsync({
      url_prefix: API_URL,
    });

    if (response.code === 200) {
      // Encode returned data as token
      return encodeURIComponent(JSON.stringify(response.data));
    } else {
      throw new Error(response.msg);
    }
  } catch (error) {
    console.error("[fetchMakePrintSign] Failed to get print signature:", error);
    throw error;
  }
};

/**
 * Hook for jumping to Yizhe system
 */
export const useYizheJump = (options: YizheJumpOptions = {}) => {
  const { onError, onSuccess } = options;
  const [isJumping, setIsJumping] = useState(false);
  const currentShop = getCurrentShopData();
  const makePrintSignMutation = usePostBaobPcSupplyMakePrintSign();

  // Build jump URL
  const buildJumpUrl = useCallback(
    (token: string, supplier: string, shopId: string | number): string => {
      const domain = getYizheDomain();
      const path = YIZHE_PATHS.PRINT_CENTER_STYLE_MANAGEMENT;
      const params = new URLSearchParams({
        from: "baoban",
        shopId: shopId.toString(),
        supplier,
        token,
      });
      return `${domain}${path}?${params.toString()}`;
    },
    [],
  );

  // Validate required parameters
  const validateParams = useCallback((): { supplier: string } | null => {
    const supplier = getToken();
    if (!supplier) {
      const error = new Error("获取 supplier 参数失败，请确保已登录");
      console.error("[useYizheJump]", error.message);
      onError?.(error);
      message.error("获取用户信息失败，请重新登录");
      return null;
    }
    return { supplier };
  }, [onError]);

  // Execute jump
  const handleYizheJump = useCallback(async () => {
    try {
      setIsJumping(true);

      // Validate required parameters
      const params = validateParams();
      if (!params) {
        setIsJumping(false);
        return;
      }

      // Get print signature
      const token = await fetchMakePrintSign(makePrintSignMutation.mutateAsync);

      if (token) {
        // Build jump URL
        const jumpUrl = buildJumpUrl(
          token,
          params.supplier,
          currentShop.shopId ?? "",
        );

        setTimeout(() => {
          // Execute jump
          window.open(jumpUrl, "_blank");

          // Reset state after jump completion
          setIsJumping(false);

          onSuccess?.();
        }, 0);
      }
    } catch (error) {
      message.error("获取打印签名失败");
    } finally {
      setIsJumping(false);
    }
  }, [
    buildJumpUrl,
    validateParams,
    makePrintSignMutation.mutateAsync,
    onSuccess,
    currentShop.shopId,
  ]);

  return {
    /** Execute jump to Yizhe system */
    handleYizheJump,
    /** Whether jumping is in progress */
    isJumping,
  };
};
